import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { format, addDays, subDays } from 'date-fns';
import { ArrowUpDown } from 'lucide-react';

interface LateBillsViewProps {
  dateFilter: string;
  utilityTypeFilter: string;
  locationFilter: string;
  accountStatusFilter: string;
}

interface LateBillRow {
  daysLate: number;
  lastBillInvoiceDate: string;
  billingAccount: string;
  locations: string;
  accountStatuses: string;
  utilityTypes: string;
  lastBillType: string;
  lastBillDueDate: string;
  daysOfServiceArrival: number;
  expectedBillDueDate: string;
  daysToNextExpectedDue: number;
}

const generateDummyData = (): LateBillRow[] => {
  const locations = [
    '40 South Terrace Dr Apt. #7',
    '3241 Sovereign Dr Apt. #7',
    '1073 Paragon Ct Apt. #12',
    '3233 Sovereign Dr Apt. #22',
    '2903 Banning Road #2',
  ];

  const baseDate = new Date(2025, 8, 5); // September 5, 2025

  return locations.map((location, idx) => {
    const daysLate = 72 - idx * 2;
    const invoiceDate = subDays(baseDate, daysLate + 30);
    const dueDate = addDays(invoiceDate, 21);
    const expectedDue = addDays(dueDate, 30);
    const daysToNext = Math.max(0, Math.floor((expectedDue.getTime() - baseDate.getTime()) / (1000 * 60 * 60 * 24)));

    return {
      daysLate,
      lastBillInvoiceDate: format(invoiceDate, 'yyyy-MM-dd'),
      billingAccount: `910${169045640 + idx * 1000}`,
      locations: location,
      accountStatuses: 'Open',
      utilityTypes: idx % 2 === 0 ? 'ELECTRIC' : 'ELECTRIC, NATURALGAS',
      lastBillType: 'live',
      lastBillDueDate: format(dueDate, 'yyyy-MM-dd'),
      daysOfServiceArrival: idx + 1,
      expectedBillDueDate: format(expectedDue, 'yyyy-MM-dd'),
      daysToNextExpectedDue: daysToNext,
    };
  });
};

export function LateBillsView({
  dateFilter,
  utilityTypeFilter,
  locationFilter,
  accountStatusFilter,
}: LateBillsViewProps) {
  const [sortColumn, setSortColumn] = useState<keyof LateBillRow>('daysLate');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const dummyData = generateDummyData();

  const sortedData = [...dummyData].sort((a, b) => {
    const aVal = a[sortColumn];
    const bVal = b[sortColumn];
    
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return sortDirection === 'asc' 
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    }
    
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    }
    
    return 0;
  });

  const handleSort = (column: keyof LateBillRow) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  const getDaysToNextColor = (days: number) => {
    if (days < 7) return 'bg-red-500';
    if (days < 14) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Late Bills</CardTitle>
            <CardDescription>Bills sorted by days late</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Input placeholder="Search..." className="w-[200px]" />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead 
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort('daysLate')}
                >
                  <div className="flex items-center gap-2">
                    Days Late
                    {sortColumn === 'daysLate' && (
                      <ArrowUpDown className="h-4 w-4" />
                    )}
                  </div>
                </TableHead>
                <TableHead>Last Bill Invoice Date</TableHead>
                <TableHead>Billing Account</TableHead>
                <TableHead>Locations</TableHead>
                <TableHead>Account Statuses</TableHead>
                <TableHead>Utility Types</TableHead>
                <TableHead>Last Bill Type</TableHead>
                <TableHead>Last Bill Due Date</TableHead>
                <TableHead>Days of Service Arrival</TableHead>
                <TableHead>Expected Bill Due Date</TableHead>
                <TableHead className="min-w-[200px]">Days To Next Expected Due</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedData.map((row, idx) => (
                <TableRow key={idx}>
                  <TableCell className="font-semibold">
                    <Badge variant={row.daysLate > 60 ? 'destructive' : row.daysLate > 30 ? 'secondary' : 'outline'}>
                      {row.daysLate}
                    </Badge>
                  </TableCell>
                  <TableCell>{row.lastBillInvoiceDate}</TableCell>
                  <TableCell className="font-mono text-sm">{row.billingAccount}</TableCell>
                  <TableCell>{row.locations}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{row.accountStatuses}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 flex-wrap">
                      {row.utilityTypes.split(', ').map((util, utilIdx) => (
                        <Badge key={utilIdx} variant="secondary" className="text-xs">
                          {util}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="default">{row.lastBillType}</Badge>
                  </TableCell>
                  <TableCell>{row.lastBillDueDate}</TableCell>
                  <TableCell>{row.daysOfServiceArrival}</TableCell>
                  <TableCell>{row.expectedBillDueDate}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div 
                        className={`h-4 rounded flex-1 ${getDaysToNextColor(row.daysToNextExpectedDue)}`}
                        style={{ width: `${Math.min(100, (row.daysToNextExpectedDue / 30) * 100)}%` }}
                      />
                      <span className="text-sm font-medium min-w-[40px]">{row.daysToNextExpectedDue}</span>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

